import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Logo } from "@/components/Shared";
import { GoogleLogo } from "@phosphor-icons/react";

const AUTH_BG = "/login-hero.jpg";

export default function Login() {
  const { login, register, formatApiError } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let u;
      if (mode === "login") {
        u = await login(email, password);
        toast.success("Welcome back");
      } else {
        u = await register(email, password, name);
        toast.success("Account created");
      }
      navigate(params.get("next") || (u?.role === "patient" ? "/clinic/portal" : "/clinic"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:block lg:w-1/2 relative bg-white" data-testid="login-hero">
        <img src={AUTH_BG} alt="Raphadon Prosthetic and Orthotic Inventory System" className="absolute inset-0 h-full w-full object-cover object-top" />
      </div>

      <div className="flex-1 flex items-center justify-center p-6 sm:p-12 bg-background">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="lg:hidden mb-8">
            <Logo height="h-20" />
          </div>

          <Link to="/" data-testid="login-back-to-site" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors duration-200 mb-4">← Back to website</Link>

          <h1 className="text-2xl font-heading font-semibold tracking-tight text-foreground">
            {mode === "login" ? "Sign in to your store" : "Create your account"}
          </h1>
          <p className="text-sm text-muted-foreground mt-1.5">
            {mode === "login" ? "Manage your P&O inventory in one place." : "Start tracking your stock in minutes."}
          </p>

          <Button
            type="button"
            variant="outline"
            onClick={googleLogin}
            data-testid="google-login-button"
            className="w-full mt-6 h-11 gap-2 border-border hover:bg-accent"
          >
            <GoogleLogo size={18} weight="bold" /> Continue with Google
          </Button>

          <div className="flex items-center gap-3 my-5">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">or</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div className="space-y-1.5">
                <Label htmlFor="name">Full name</Label>
                <Input id="name" data-testid="name-input" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Jane Doe" className="h-11" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="email-input" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@clinic.com" className="h-11" />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                {mode === "login" && (
                  <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-primary hover:underline">Forgot password?</Link>
                )}
              </div>
              <Input id="password" type="password" data-testid="password-input" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" className="h-11" />
            </div>
            <Button type="submit" disabled={busy} data-testid="submit-auth-button" className="w-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground">
              {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground mt-6 text-center">
            {mode === "login" ? "New here? " : "Already have an account? "}
            <button
              type="button"
              data-testid="toggle-auth-mode"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="text-primary font-medium hover:underline"
            >
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
